/* eslint-disable @typescript-eslint/no-floating-promises */
import React from 'react';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet, fetchJson } from '../../store/fetch';
import { appContext } from '../appContext';
import {
    Content,
    EditPanel,
    EditPanelCell,
    EditPanelInput,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    ISelectOption,
    Panel,
    PanelBody,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TextWithLineBreaks,
    TopbarCrumbActive,
    TopbarLeftOnly,
    Tray,
} from '../elements';
import { DropzoneComponent } from '../utils/dropzone/DropzoneComponent';
import { NullableBoolean } from '../utils/NullableBoolean';

interface IUpload {
    filename: string;
    isSuccessful: boolean;
    message: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ImportObjects = () => {
    const translate = useTranslate();
    const currentAppContext = React.useContext(appContext.context);
    const [uploads, setUploads] = React.useState<IUpload[]>([]);
    const [clientCompanies, setClientCompanies] = React.useState<ISelectOption[]>([]);
    const [ownerCompanies, setOwnerCompanies] = React.useState<ISelectOption[]>([]);
    const [surveyorCompanies, setSurveyorCompanies] = React.useState<ISelectOption[]>([]);

    const [objectTypes, setObjectTypes] = React.useState<ISelectOption[]>([]);
    const [importType, setImportType] = React.useState<undefined | string>();
    const [clientCompanyId, setClientCompanyId] = React.useState<undefined | string>();
    const [ownerCompanyId, setOwnerCompanyId] = React.useState<undefined | string>();
    const [surveyorCompanyId, setSurveyorCompanyId] = React.useState<undefined | string>();
    const [objectTypeId, setObjectTypeId] = React.useState<undefined | string>();
    const [codePrefix, setCodePrefix] = React.useState<undefined | string>();
    const [location, setLocation] = React.useState<undefined | string>();
    const [inspectionStandards, setInspectionStandards] = React.useState<ISelectOption[]>([]);
    const [inspectionStandardId, setInspectionStandardId] = React.useState<undefined | string>();
    const [dropzonePostUrl, setDropzonePostUrl] = React.useState('/api/objects/import');
    const { languageCode } = currentAppContext;

    const importTypes = [
        { value: 'importDefaultExcel', text: 'Import Excel' },
        { value: 'importRWSExcel', text: 'Import Excel (RWS format)' },
        { value: 'importAutoCad', text: 'Import Autocad Map' },
        { value: 'importJson', text: 'Import Json' },
        { value: 'importCsvWithMapping', text: 'Import CSV with mapping' },
        { value: 'importDecompositionCsvWithMapping', text: 'Import decomposition CSV with mapping' },
    ];

    function onUploadComplete(result: any) {
        if (result.xhr.status !== 200) {
            let errorMessage = '500';
            try {
                const { responseText } = result.xhr;
                const response = JSON.parse(responseText);
                if (response.message) {
                    errorMessage = response.message;
                } else if (response.messages) {
                    errorMessage = response.messages.join(',');
                }
            } catch {}
            setUploads((previous) => [...previous, { filename: result.name, isSuccessful: false, message: errorMessage }]);
        } else {
            const { responseText } = result.xhr;
            const response = JSON.parse(responseText);
            let message: string;
            if (response.messages) {
                message = response.messages.join(',');
            } else {
                // eslint-disable-next-line prefer-destructuring
                message = response.message;
            }
            setUploads((previous) => [...previous, { filename: result.name, isSuccessful: response.isSuccessful, message }]);
        }
    }

    React.useEffect(() => {
        (async () => {
            const responseClientCompanies = await fetchJson('/api/clients', 'GET');
            setClientCompanies(
                responseClientCompanies.json.items.map((company: { id: string; name: string }) => ({ text: company.name, value: company.id })),
            );
        })();
    }, [currentAppContext.languageCode]);

    React.useEffect(() => {
        (async () => {
            const responseObjectTypes = await fetchGet('/api/objectTypesList', { languageCode: currentAppContext.languageCode, clientCompanyId });
            setObjectTypes(
                responseObjectTypes.json.map((objectType: { id: string; name: string }) => ({ text: objectType.name, value: objectType.id })),
            );
        })();
    }, [currentAppContext.languageCode, clientCompanyId]);

    React.useEffect(() => {
        if (clientCompanyId) {
            (async () => {
                const responseOwnerCompanies = await fetchJson(`/api/companyList?type=owner&companyid=${clientCompanyId}`, 'GET');
                setOwnerCompanies(
                    responseOwnerCompanies.json.items.map((company: { id: string; name: string }) => ({ text: company.name, value: company.id })),
                );

                const responseSurveyorCompanies = await fetchJson(`/api/companyList?type=owner&companyid=${clientCompanyId}`, 'GET');
                setSurveyorCompanies(
                    responseSurveyorCompanies.json.items.map((company: { id: string; name: string }) => ({ text: company.name, value: company.id })),
                );
            })();
        }
    }, [clientCompanyId]);

    React.useEffect(() => {
        if (objectTypeId) {
            (async () => {
                const inspectionStandardsResponse = (await fetchGet('/api/inspectionStandards', {
                    objectTypeId,
                    languageCode: currentAppContext.languageCode,
                })) as { json: { id: string; name: string }[] };
                setInspectionStandards(inspectionStandardsResponse.json.map((i) => ({ text: i.name, value: i.id })));
            })();
        }
    }, [objectTypeId, currentAppContext.languageCode]);

    React.useEffect(() => {
        const query = new URLSearchParams();
        if (importType) {
            query.set('importType', importType);
        }
        if (codePrefix) {
            query.set('codePrefix', codePrefix);
        }
        if (location) {
            query.set('location', location);
        }
        if (clientCompanyId) {
            query.set('clientCompanyId', clientCompanyId);
        }
        if (ownerCompanyId) {
            query.set('ownerCompanyId', ownerCompanyId);
        }
        if (surveyorCompanyId) {
            query.set('surveyorCompanyId', surveyorCompanyId);
        }
        if (objectTypeId) {
            query.set('objectTypeId', objectTypeId);
        }
        if (inspectionStandardId) {
            query.set('inspectionStandardId', inspectionStandardId);
        }
        query.set('languageCode', languageCode);
        setDropzonePostUrl(`/api/objects/import?${query.toString()}`);
    }, [importType, codePrefix, location, clientCompanyId, ownerCompanyId, surveyorCompanyId, objectTypeId, inspectionStandardId, languageCode]);

    let isDropzoneVisible = false;
    if (importType) {
        if (importType === 'importDefaultExcel') {
            if (!clientCompanyId || !ownerCompanyId || !inspectionStandardId) {
                isDropzoneVisible = false;
            } else {
                isDropzoneVisible = true;
            }
        } else if (importType === 'importCsvWithMapping' || importType === 'importDecompositionCsvWithMapping') {
            if (!clientCompanyId) {
                isDropzoneVisible = false;
            } else {
                isDropzoneVisible = true;
            }
        } else {
            isDropzoneVisible = true;
        }
    }

    return (
        <>
            <TopbarLeftOnly>
                <TopbarCrumbActive title={translate('common.importItem', translate('common.objects'))} icon="fa-upload" />
            </TopbarLeftOnly>
            <Content>
                <Tray>
                    <Panel>
                        <PanelBody>
                            <EditPanel>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={6}>
                                        <EditPanelSelect
                                            name="importType"
                                            placeholder={translate('common.selectItem', 'Import type')}
                                            value={importType}
                                            options={importTypes}
                                            handleChange={(value) => setImportType(value)}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                {importType === 'importDefaultExcel' && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={8}>
                                            <a href="/assets/import-templates/OBJECT-IMPORT-TEMPLATE.xlsx" download>
                                                Template
                                            </a>
                                            <br />
                                            <a href="/assets/import-templates/OBJECT-IMPORT-TEMPLATE-EPSG-28992.xlsx" download>
                                                Template EPSG:28992 (Rijksdriehoeksstelsel)
                                            </a>
                                            <br />
                                            <a href="/assets/import-templates/OBJECT-IMPORT-TEMPLATE-OBJECT-TYPE-FIELDS.xlsx" download>
                                                Template with object type fields
                                            </a>
                                            <ul>
                                                <li>Object.Code is required and should be unique.</li>
                                                <li>Object.Name is required.</li>
                                                <li>Object.Location is required.</li>
                                                <li>All other columns are optional.</li>
                                                <li>When importing object type fields, all fields must be present in the Excel file.</li>
                                                <li>
                                                    Only use the letters and numbers of the name of the fields, eg the field &quot;Use data from
                                                    county/muni.?&quot; needs the header &quot;Object.Fields.Use_data_from_countymuni&quot;
                                                </li>
                                            </ul>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}
                                {importType === 'importCsvWithMapping' && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={8}>
                                            Tar file with objects.csv and mapping file, can include shape files in &quot;shapes&quot; folder.
                                            <br />
                                            <code>tar -cvf output.tar mapping.jsonc objects.csv shapes/</code>
                                            <br />
                                            <a href="/assets/import-templates/object-mapping.jsonc" download>
                                                Example mapping file
                                            </a>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}

                                {importType === 'importDecompositionCsvWithMapping' && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={8}>
                                            Tar file with objects.csv and mapping file.
                                            <code>tar -cvf output.tar mapping.jsonc decomposition.csv</code>
                                            <br />
                                            <a href="/assets/import-templates/decomposition-mapping.jsonc" download>
                                                Example mapping file
                                            </a>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}
                                {importType && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSelect
                                                name="clientCompanyId"
                                                placeholder={translate('common.selectItem', translate('common.client'))}
                                                value={clientCompanyId}
                                                options={clientCompanies}
                                                handleChange={(value) => setClientCompanyId(value)}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}
                                {clientCompanyId && importType !== 'importCsvWithMapping' && importType !== 'importDecompositionCsvWithMapping' && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSelect
                                                name="objectTypeId"
                                                placeholder={translate('common.selectItem', translate('common.objectTypes'))}
                                                value={objectTypeId}
                                                options={objectTypes}
                                                handleChange={(value) => setObjectTypeId(value)}
                                            />
                                        </EditPanelCell>
                                        {objectTypeId && (
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSelect
                                                    name="inspectionStandardId"
                                                    placeholder={translate('common.selectItem', translate('common.inspectionStandard'))}
                                                    value={inspectionStandardId}
                                                    options={inspectionStandards}
                                                    handleChange={(value) => setInspectionStandardId(value)}
                                                />
                                            </EditPanelCell>
                                        )}
                                    </EditPanelRow>
                                )}

                                {clientCompanyId && importType !== 'importDecompositionCsvWithMapping' && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSelect
                                                name="ownerCompanyId"
                                                placeholder={translate('common.selectItem', translate('common.owner'))}
                                                value={ownerCompanyId}
                                                options={ownerCompanies}
                                                handleChange={(value) => setOwnerCompanyId(value)}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSelect
                                                name="surveyorCompanyId"
                                                placeholder={translate('common.selectItem', translate('common.surveyor'))}
                                                value={surveyorCompanyId}
                                                options={surveyorCompanies}
                                                handleChange={(value) => setSurveyorCompanyId(value)}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}
                                {importType === 'importAutoCad' && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelInput
                                                name="location"
                                                placeholder={translate('common.location')}
                                                value={location}
                                                handleChange={(value) => setLocation(value)}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={3}>
                                            <EditPanelInput
                                                name="codePrefix"
                                                placeholder="Code prefix"
                                                value={codePrefix}
                                                handleChange={(value) => setCodePrefix(value)}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}
                                {isDropzoneVisible && (
                                    <EditPanelSection>
                                        <div className="tray-bin p10 m5" style={{ minHeight: 250 }}>
                                            <h5 className="text-muted mt10 fw600 pl10">
                                                <i className="fa fa-exclamation-circle text-info fa-lg pr10" />
                                                {translate('survey.dragAndDropUploader')}{' '}
                                            </h5>
                                            <DropzoneComponent
                                                className="dropzone-sm"
                                                config={{
                                                    postUrl: dropzonePostUrl,
                                                }}
                                                djsConfig={{
                                                    parallelUploads: 1,
                                                    acceptedFiles: '.csv,.json,.tar,.xlsx',
                                                }}
                                                eventHandlers={{
                                                    complete: onUploadComplete,
                                                }}
                                            />
                                        </div>
                                    </EditPanelSection>
                                )}
                                <PanelTable>
                                    <TableHead>
                                        <TableHeadRow>
                                            <TableHeader>Filename</TableHeader>
                                            <TableHeader>Successful</TableHeader>
                                            <TableHeader>Message</TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    <TableBody>
                                        {uploads.map((item, i) => (
                                            <TableRow key={i}>
                                                <TableData>{item.filename}</TableData>
                                                <TableData>
                                                    <NullableBoolean value={item.isSuccessful} />
                                                </TableData>
                                                <TableData>
                                                    <TextWithLineBreaks text={item.message}></TextWithLineBreaks>
                                                </TableData>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </PanelTable>
                            </EditPanel>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
};
